import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useEmailGrantTokensMutation,
  useEmailVerificationMutation,
  useEmailVerificationUrlsMutation,
  useVerifyOneTimePasswordMutation,
} from 'store/api/yay/user'
import { useCountDown } from 'hooks'
import { concealEmail } from '../EmailSetting/helpers'

import { CodeInput } from 'components'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import Button from 'components/Button'

import '../style.scss'

type Props = {
  email: string
  mode?: 'password_reset' | 'otp'
  concealEmail?: boolean
  onPrev?: () => void
  onNext: (code: string) => void
}

const CODE_LENGTH = 6
const MAX_ATTEMPT = 9

export const VerifyEmailForm = ({
  email,
  mode,
  concealEmail: _concealEmail,
  onPrev,
  onNext,
}: Props) => {
  const { t, i18n } = useTranslation()
  const [isSendFirstTime, setIsSendFirstTime] = useState(true)
  const [code, setCode] = useState('')
  const [wrongAttempt, setWrongAttempt] = useState(0)
  const [errorText, setErrorText] = useState('')
  const { count, startCountDown, stopCountDown } = useCountDown()

  const [getEmailVerificationUrl] = useEmailVerificationUrlsMutation()
  const [sendEmailVerification] = useEmailVerificationMutation()
  const [emailGrantTokens, emailGrantTokensRes] = useEmailGrantTokensMutation()
  const [verifyOneTimePassword, verifyOneTimePasswordRes] =
    useVerifyOneTimePasswordMutation()

  useEffect(() => {
    sendEmail(true)

    return () => {
      stopCountDown()
    }
  }, [])

  const sendEmail = async (firstTime?: boolean) => {
    let nextCountdown = 60

    if (email) {
      const url = await getEmailVerificationUrl({
        email,
        locale: i18n.language,
        intent: mode,
      })

      if ('data' in url) {
        const sendResult = await sendEmailVerification(url.data)

        if ('error' in sendResult && 'data' in sendResult.error) {
          try {
            nextCountdown = sendResult.error.data.retry_in
          } catch (e) {
            console.log(e)
          }
        }

        if ('data' in sendResult && sendResult.data) {
          setCode('')
          setErrorText('')
        }
      }
    }

    startCountDown(nextCountdown)
    if (!firstTime) setIsSendFirstTime(false)
  }

  const submitCode = async () => {
    const result = await emailGrantTokens({ code, email })

    if ('data' in result) {
      const emailGrantToken = result.data

      setWrongAttempt(0)

      if (mode === 'otp') {
        const verifyResult = await verifyOneTimePassword({
          emailGrantToken,
        })

        if ('data' in verifyResult) {
          onNext(result.data)
        }
      } else {
        onNext(result.data)
      }
    } else {
      setWrongAttempt((x) => x + 1)
      setErrorText(
        t('user_setting.verify_form.error_email_verification_incorrect_code')
      )
    }
  }

  return (
    <>
      <div className="UserSetting__code-verification">
        <h3 className="UserSetting__code-verification__h3">
          {isSendFirstTime
            ? t('user_setting.email_setting.email_confirm_sent_code')
            : t('user_setting.email_setting.email_confirm_sent_code_again')}
        </h3>

        <p className="UserSetting__code-verification__p">
          {t('user_setting.email_setting.email_confirm_enter_code')}

          <br />

          <span className="UserSetting__code-verification__p__span">
            {_concealEmail ? concealEmail(email) : email}
          </span>

          {onPrev && (
            <>
              {' '}
              <span
                className="UserSetting__code-verification__a"
                onClick={onPrev}
              >
                {t('common.change').toLowerCase()}
              </span>
            </>
          )}
        </p>

        <div className="UserSetting__code-verification__code">
          <CodeInput
            value={code}
            setValue={setCode}
            length={CODE_LENGTH}
            error={errorText}
            clearError={() => setErrorText('')}
            disabled={wrongAttempt > MAX_ATTEMPT}
          />
        </div>

        <p className="UserSetting__code-verification__p">
          {count === null || count <= 0 ? (
            <a
              className="UserSetting__code-verification__a"
              onClick={() => sendEmail()}
            >
              {t('user_setting.verify_form.email_confirm_resend')}
            </a>
          ) : (
            <span>
              {t('user_setting.verify_form.email_confirm_resend_in', {
                time: timeText(count),
              })}
            </span>
          )}
        </p>

        <ConfirmBoxBasic
          isActive={wrongAttempt > MAX_ATTEMPT}
          title={t(
            'user_setting.verify_form.error_email_verification_incorrect_code_multiple_time.title'
          )}
          content={t(
            'user_setting.verify_form.error_email_verification_incorrect_code_multiple_time.message'
          )}
          onClose={() => setWrongAttempt(MAX_ATTEMPT)}
          onOk={() => setWrongAttempt(MAX_ATTEMPT)}
        />
      </div>

      <div className="UserSetting__form">
        <Button
          className="UserSetting__form__submit"
          size="medium"
          type="submit"
          onClick={() => {
            submitCode()
          }}
          disabled={
            emailGrantTokensRes?.isLoading ||
            code?.length < CODE_LENGTH ||
            verifyOneTimePasswordRes?.isLoading
          }
        >
          {emailGrantTokensRes?.isLoading || verifyOneTimePasswordRes?.isLoading
            ? t('common.loading')
            : t('common.next')}
        </Button>
      </div>
    </>
  )
}

const timeText = (time: number) => {
  const min = Math.floor(time / 60)
  const sec = Math.floor(time % 60)

  return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}
